import { Fragment, useEffect, useState } from "react";
import ModernPopupHeader from "../common/ModernPopupHeader";
import ModalModern from "../ModalModern";
import ModernPopupBody from "../common/ModernPopupBody";
import {
  getIconSrc,
  getInitialsIconSrc,
} from "../../../../utils/images/iconUtils";
import {
  getAccountLabel,
  getMerchantName,
  mapTransactionToRow,
} from "./utils/helpers";
import CategoryPill from "../../badges/CategoryPill";
import { updateTransaction } from "../../../../api/finances/transactionsAPI";
import { useAppToast } from "../../../../hooks/useAppToast";

function TransactionDetailsModal({
  transaction,
  onClose,
  onTransactionUpdate,
  copy,
  commonCopy,
}) {
  const [currentTransaction, setCurrentTransaction] = useState(transaction);
  const [isUpdatingTaxDeductible, setIsUpdatingTaxDeductible] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [noteValue, setNoteValue] = useState(transaction?.note || "");
  const [updateError, setUpdateError] = useState("");
  const { toastMessage, showToast } = useAppToast();

  useEffect(() => {
    setCurrentTransaction(transaction);
    setNoteValue(transaction?.note || "");
    setUpdateError("");
  }, [transaction]);

  const handleTaxDeductibleChange = async (event) => {
    const nextValue = event.target.checked;
    const previousTransaction = currentTransaction;

    setUpdateError("");
    setIsUpdatingTaxDeductible(true);
    setCurrentTransaction((prevTransaction) => ({
      ...prevTransaction,
      tax_deductible: nextValue,
    }));

    try {
      const updatedTransaction = await updateTransaction(
        currentTransaction.uuid,
        {
          tax_deductible: nextValue,
        },
      );
      const mappedTransaction = mapTransactionToRow(updatedTransaction);
      setCurrentTransaction(mappedTransaction);
      if (onTransactionUpdate) {
        onTransactionUpdate(mappedTransaction);
      }
      showToast(
        nextValue ? copy?.marked_tax_deductible : copy?.unmarked_tax_deductible,
      );
    } catch (error) {
      setCurrentTransaction(previousTransaction);
      setUpdateError(error.message || copy?.unable_to_update_tax_deductible);
    } finally {
      setIsUpdatingTaxDeductible(false);
    }
  };

  const handleSaveNote = async () => {
    setUpdateError("");
    setIsSavingNote(true);

    try {
      const updatedTransaction = await updateTransaction(
        currentTransaction.uuid,
        {
          note: noteValue,
        },
      );
      const mappedTransaction = mapTransactionToRow(updatedTransaction);
      setCurrentTransaction(mappedTransaction);
      setNoteValue(mappedTransaction.note || "");
      if (onTransactionUpdate) {
        onTransactionUpdate(mappedTransaction);
      }
      showToast(commonCopy?.note_saved);
    } catch (error) {
      setUpdateError(error.message || copy?.unable_to_save_note);
    } finally {
      setIsSavingNote(false);
    }
  };

  if (!currentTransaction) return null;

  const merchantName =
    getMerchantName(currentTransaction) || copy?.fallback_title;
  const category = currentTransaction?.category || commonCopy?.uncategorized;
  const categoryCode =
    currentTransaction?.category_code || currentTransaction?.category_value;
  const amount = currentTransaction?.amount;
  const isPending =
    currentTransaction?.pending || currentTransaction?.status === "pending";
  const statusLabel = isPending ? copy?.pending_status : copy?.completed_status;
  const statusClass = isPending ? "type-bill" : "type-pill type-subscription";
  const merchantIcon =
    currentTransaction?.logo_url || currentTransaction?.merchant_icon;
  const merchantIconSrc = merchantIcon
    ? getIconSrc(merchantIcon)
    : getInitialsIconSrc(merchantName);
  const hasNoteChanges = noteValue !== (currentTransaction.note || "");

  return (
    <Fragment>
      <ModalModern onClose={onClose}>
        <ModernPopupHeader onClose={onClose}>
          <div className="header-content-modern-row">
            <div>
              <h3 className="transaction-popup-title">
                {getAccountLabel(currentTransaction)}
              </h3>
            </div>
          </div>
        </ModernPopupHeader>
        <ModernPopupBody>
          <div className="mp-col-container">
            <div className="mp-row-container mobile-to-col padding-reset">
              <div className="mp-transacion-metadata">
                <div
                  className="mp-col-container"
                  style={{
                    gap: "24px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    <span
                      className={statusClass}
                      style={{ maxWidth: "120px", textAlign: "center" }}
                    >
                      {statusLabel}
                    </span>
                    <div className="mp-transaction-merchant-status">
                      <div className="mp-merchant-icon">
                        <img src={merchantIconSrc} alt={merchantName} />
                      </div>
                      <div className="mp-merchant-name-status">
                        <div>
                          <p>{merchantName}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mp-transaction-amount-category">
                    <div>
                      <span className="mp-transaction-amount">{amount}</span>
                    </div>
                    <div>
                      <CategoryPill
                        label={category}
                        categoryCode={categoryCode}
                        categoryPrimary={currentTransaction.category_primary}
                        categoryDetailed={currentTransaction.category_detailed}
                      />
                    </div>
                  </div>
                  <div>
                    <p
                      style={{
                        color: "#33393f",
                        fontSize: "16px",
                        fontWeight: "300",
                        fontStyle: "normal",
                        lineHeight: "120%",
                      }}
                    >
                      {currentTransaction.display_description}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mp-transacion-actions">
                <p>{commonCopy?.actions}</p>
                <div className="mp-transaction-tax-deductible">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/images/icons/calculator.svg`}
                      alt={copy?.calculator_icon_alt}
                    />
                    <span>{copy?.tax_deductible}</span>
                  </div>
                  <div className="switch-container">
                    <input
                      type="checkbox"
                      id={`tax-deductible-${currentTransaction.uuid}`}
                      className="edit-switch"
                      checked={Boolean(currentTransaction.tax_deductible)}
                      disabled={isUpdatingTaxDeductible}
                      onChange={handleTaxDeductibleChange}
                    />
                    <label
                      htmlFor={`tax-deductible-${currentTransaction.uuid}`}
                      className="switch-label"
                    >
                      <span className="switch-slider"></span>
                    </label>
                  </div>
                </div>
                <div
                  className="mp-transaction-notes"
                  style={{ marginTop: "24px" }}
                >
                  <textarea
                    placeholder={commonCopy?.add_note}
                    value={noteValue}
                    onChange={(event) => setNoteValue(event.target.value)}
                    disabled={isSavingNote}
                    style={{ width: "100%" }}
                  ></textarea>
                  {hasNoteChanges && (
                    <div
                      style={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <button
                        type="button"
                        className="cta-button primary rounded small"
                        disabled={isSavingNote}
                        onClick={handleSaveNote}
                        style={{ marginTop: "12px" }}
                      >
                        {isSavingNote
                          ? commonCopy?.saving
                          : commonCopy?.save_note}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </ModernPopupBody>
      </ModalModern>
      {toastMessage && (
        <div
          role="status"
          aria-live="polite"
          className="app-toast app-toast-primary"
        >
          {toastMessage}
        </div>
      )}
    </Fragment>
  );
}

export default TransactionDetailsModal;
